<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'

import TopbarNavigationSearch from './TopbarNavigationSearch.vue'

import { useNavStore } from '@/app/navigation'
import { AccountArea, type WorkspaceItem, useRbac } from '@/features/account'
import { AuthBrandLogo, useSession } from '@/features/auth'
import { AiCreditSlot, usePlanSelection } from '@/features/credits'
import { openOnboarding, WhatsNewButton } from '@/features/global-surface'
import {
  INSIGHTS_PREVIEW_ROUTE,
  ReportInsightsPanel,
  type InsightResult,
} from '@/features/insights'
import { useEnv } from '@/shared/config'
import { useI18n } from '@/shared/i18n'
import { useToast } from '@/shared/ui/feedback'
import { Icon } from '@/shared/ui/icon'
import { ThemeToggle } from '@/shared/ui/theme'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const navStore = useNavStore()
const session = useSession()
const rbac = useRbac()
const planSelection = usePlanSelection()
const toast = useToast()
const loggingOut = ref(false)
const isMobile = ref(false)
let navigationMedia: MediaQueryList | null = null

const navigationOpen = computed(() =>
  isMobile.value ? navStore.isPinnedMobileSide : navStore.isPinnedSide,
)
const navigationLabel = computed(() =>
  navigationOpen.value ? t('shell.topbar.closeNavigation') : t('shell.topbar.openNavigation'),
)
const navigationTarget = computed(() =>
  isMobile.value ? 'app-navigation-mobile' : 'app-navigation-desktop',
)
const showNavigationToggle = computed(
  () =>
    !planSelection.requested.value &&
    (route.meta.topbarNavigation !== 'mobile-only' || isMobile.value),
)
const showInsights = computed(() => true)
// The admin panel stays in v3 on its own origin for good (module 19 is not built in v4 —
// `router/ingress/pendingCutover.ts` keeps `/admin/*` as a PERMANENT `legacyHardNavigation`), so
// there is no v4 route to push and the click must be a real address transition. Unconfigured host
// = no admin panel for this deployment: render nothing rather than a control that cannot work.
const adminUrl = computed(() => useEnv().API_ADMIN_URL)
const showAdmin = computed(() => rbac.isSysAdmin.value && adminUrl.value !== undefined)
function syncNavigationMedia(event: MediaQueryList | MediaQueryListEvent): void {
  isMobile.value = event.matches
}

onMounted(() => {
  navigationMedia = window.matchMedia('(max-width: 56rem)')
  syncNavigationMedia(navigationMedia)
  navigationMedia.addEventListener('change', syncNavigationMedia)
})

onUnmounted(() => navigationMedia?.removeEventListener('change', syncNavigationMedia))

function toggleNavigation(): void {
  if (isMobile.value) navStore.toggleMobileSidebar()
  else navStore.toggleSidebar()
}

async function logout(): Promise<void> {
  loggingOut.value = true
  try {
    await session.logout()
    await router.replace({ name: 'Login' })
  } finally {
    loggingOut.value = false
  }
}

/** "New workspace" — v3 `WorkspaceSelect.vue:66-69` opened the onboarding takeover in place. */
function startOnboarding(): void {
  openOnboarding()
}

async function openWorkspaceSettings(_workspace: WorkspaceItem): Promise<void> {
  if (!router.hasRoute('WorkspaceSettings')) {
    toast.warning(t('shell.topbar.workspaceSettingsUnavailable'))
    return
  }
  await router.push({ name: 'WorkspaceSettings' })
}

/** Leaves the SPA outright — the target origin applies its own v3 session, none is bridged. */
function openAdminPanel(): void {
  const url = adminUrl.value
  if (url !== undefined) window.location.assign(url)
}

async function openInsights(insight?: InsightResult): Promise<void> {
  if (!router.hasRoute(INSIGHTS_PREVIEW_ROUTE.name)) {
    toast.warning(t('shell.topbar.insightsUnavailable'))
    return
  }
  await router.push({
    name: INSIGHTS_PREVIEW_ROUTE.name,
    ...(insight ? { query: { insight_id: String(insight.id) } } : {}),
  })
}
</script>

<template>
  <header
    class="app-topbar sticky top-0 z-sticky flex h-[var(--ui-shell-topbar-height)] items-center gap-[var(--ui-shell-topbar-gap)] bg-surface px-[var(--ui-shell-topbar-inline-inset)] shadow-input max-[56rem]:px-md max-[30rem]:gap-sm"
  >
    <div
      class="app-topbar__brand-area flex flex-none items-center gap-[var(--ui-shell-topbar-brand-gap)]"
    >
      <button
        v-if="showNavigationToggle"
        type="button"
        class="app-topbar__navigation-toggle grid h-8 w-[var(--ui-shell-topbar-menu-width)] cursor-pointer place-items-center border-0 bg-transparent p-0 text-primary"
        :aria-label="navigationLabel"
        :aria-expanded="navigationOpen"
        :aria-controls="navigationTarget"
        @click="toggleNavigation"
      >
        <Icon name="menu" weight="light" :size="20" aria-hidden="true" />
      </button>
      <RouterLink
        class="app-topbar__brand flex h-[var(--ui-shell-topbar-control-height)] w-[var(--ui-shell-topbar-brand-width)] items-center no-underline"
        :to="{ name: 'Summary' }"
      >
        <AuthBrandLogo />
      </RouterLink>
    </div>

    <div class="app-topbar__tools flex min-w-0 flex-1 items-center gap-6 max-[30rem]:hidden">
      <AiCreditSlot v-if="!planSelection.requested.value" />

      <button
        v-if="showAdmin"
        type="button"
        class="app-topbar__admin inline-flex h-[var(--ui-shell-topbar-control-height)] min-w-[8.25rem] flex-none cursor-pointer items-center justify-center gap-2xs rounded-[var(--ui-shell-topbar-control-radius)] border border-border bg-surface px-md text-sm font-semibold leading-5 text-primary-muted [font:inherit] max-[76rem]:hidden"
        @click="openAdminPanel"
      >
        {{ t('shell.topbar.adminPanel') }}
      </button>

      <TopbarNavigationSearch v-if="!planSelection.requested.value" />
    </div>

    <div
      class="app-topbar__account flex flex-none items-center justify-end gap-[var(--ui-shell-topbar-account-gap)]"
    >
      <ThemeToggle
        :light-label="t('shell.topbar.theme.switchToLight')"
        :dark-label="t('shell.topbar.theme.switchToDark')"
      />
      <ReportInsightsPanel v-if="showInsights" @open-all="openInsights()" @open="openInsights" />
      <WhatsNewButton />
      <AccountArea
        :logging-out="loggingOut"
        :logout-label="t('shell.topbar.logout')"
        @logout="logout"
        @onboard="startOnboarding"
        @workspace-settings="openWorkspaceSettings"
      />
    </div>
  </header>
</template>

<style scoped>
.app-topbar__brand :deep(.auth-brand-logo) {
  height: auto;
  max-height: 2.5rem;
  width: 100%;
  max-width: none;
  object-fit: contain;
}

.app-topbar__tools :deep(.ai-credit-slot) {
  min-height: var(--ui-shell-topbar-credit-height);
  gap: var(--ui-shell-topbar-credit-gap);
  padding-inline: var(--ui-shell-topbar-credit-padding-inline);
  color: var(--ui-shell-topbar-credit-font-size);
  line-height: var(--ui-shell-topbar-credit-line-height);
}
.app-topbar__tools :deep(.ai-credit-slot:not(.ai-credit-slot--trial)) {
  width: var(--ui-shell-topbar-credit-width);
}

.app-topbar__account :deep(.account-area__trigger) {
  min-width: var(--ui-shell-topbar-account-min-width);
}
/* Below the tablet break the trigger collapses to its avatar so the topbar still fits. */
@media (width < 56rem) {
  .app-topbar__account :deep(.account-area__trigger) {
    min-width: 2.5rem;
  }
}
.app-topbar__account :deep(.app-topbar__insights) {
  height: var(--ui-shell-topbar-insights-height);
  width: var(--ui-shell-topbar-insights-width);
  min-width: var(--ui-shell-topbar-insights-min-width);
  gap: var(--ui-shell-topbar-insights-gap);
  border-radius: var(--ui-shell-topbar-control-radius);
  border-color: var(--ui-shell-topbar-insights-accent);
  background-color: var(--ui-shell-topbar-insights-bg);
  padding-inline: var(--ui-shell-topbar-insights-padding-inline);
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 700;
  color: var(--ui-shell-topbar-insights-accent);
}
.app-topbar__account :deep(.report-insights-panel__count) {
  height: auto;
  min-width: 0;
  border-radius: 0;
  background-color: transparent;
  padding: 0;
  font-family: inherit;
  color: inherit;
}
.app-topbar__account :deep(.whats-new-button) {
  flex: none;
}
</style>
